/**
 * Country code mapping for localized earnings lookup.
 * Keys in localized_earnings use short codes (UK, US, etc).
 * User's country in GlobalSettings uses full names (United Kingdom, etc).
 */

const COUNTRY_TO_CODE = {
  'United Kingdom': 'UK',
  'United States': 'US',
  'Canada': 'CA',
  'Australia': 'AU',
  'Germany': 'DE',
  'France': 'FR',
  'Spain': 'ES',
  'Italy': 'IT',
  'Portugal': 'PT',
  'Netherlands': 'NL',
  'Belgium': 'BE',
  'Switzerland': 'CH',
  'Austria': 'AT',
  'Sweden': 'SE',
  'Norway': 'NO',
  'Denmark': 'DK',
  'Finland': 'FI',
  'Ireland': 'IE',
  'Poland': 'PL',
  'Czech Republic': 'CZ',
  'India': 'IN',
  'Japan': 'JP',
  'South Korea': 'KR',
  'Singapore': 'SG',
  'Hong Kong': 'HK',
  'Taiwan': 'TW',
  'China': 'CN',
  'Thailand': 'TH',
  'Vietnam': 'VN',
  'Philippines': 'PH',
  'Indonesia': 'ID',
  'Malaysia': 'MY',
  'Brazil': 'BR',
  'Mexico': 'MX',
  'Argentina': 'AR',
  'Colombia': 'CO',
  'Chile': 'CL',
  'Peru': 'PE',
  'United Arab Emirates': 'AE',
  'Saudi Arabia': 'SA',
  'Qatar': 'QA',
  'Kuwait': 'KW',
  'Israel': 'IL',
  'Turkey': 'TR',
  'South Africa': 'ZA',
  'Nigeria': 'NG',
  'Kenya': 'KE',
  'Egypt': 'EG',
  'Morocco': 'MA',
  'New Zealand': 'NZ',
  'Russia': 'RU',
};

/**
 * Currency symbols for each supported currency code.
 */
const CURRENCY_SYMBOLS = {
  GBP: '£', USD: '$', EUR: '€', CAD: 'C$', AUD: 'A$', NZD: 'NZ$',
  INR: '₹', JPY: '¥', KRW: '₩', SGD: 'S$', HKD: 'HK$', CHF: 'CHF',
  SEK: 'kr', NOK: 'kr', DKK: 'kr', PLN: 'zł', CZK: 'Kč', BRL: 'R$',
  MXN: 'MX$', ZAR: 'R', AED: 'AED', SAR: 'SAR', TRY: '₺', RUB: '₽',
  THB: '฿', PHP: '₱', IDR: 'Rp', MYR: 'RM', TWD: 'NT$', CNY: '¥',
  NGN: '₦', KES: 'KSh', EGP: 'E£', MAD: 'MAD', QAR: 'QAR', KWD: 'KD',
  ILS: '₪', COP: 'COL$', CLP: 'CLP', PEN: 'S/.', ARS: 'AR$',
  VND: '₫',
};

/**
 * Map currency codes to country names for reverse lookups.
 */
const CURRENCY_FOR_COUNTRY = {
  'United Kingdom': 'GBP', 'United States': 'USD', 'Canada': 'CAD',
  'Australia': 'AUD', 'New Zealand': 'NZD', 'Germany': 'EUR',
  'France': 'EUR', 'Spain': 'EUR', 'Italy': 'EUR', 'Portugal': 'EUR',
  'Netherlands': 'EUR', 'Belgium': 'EUR', 'Austria': 'EUR',
  'Finland': 'EUR', 'Ireland': 'EUR', 'Switzerland': 'CHF',
  'Sweden': 'SEK', 'Norway': 'NOK', 'Denmark': 'DKK', 'Poland': 'PLN',
  'Czech Republic': 'CZK', 'India': 'INR', 'Japan': 'JPY',
  'South Korea': 'KRW', 'Singapore': 'SGD', 'Hong Kong': 'HKD',
  'Taiwan': 'TWD', 'China': 'CNY', 'Thailand': 'THB', 'Vietnam': 'VND',
  'Philippines': 'PHP', 'Indonesia': 'IDR', 'Malaysia': 'MYR',
  'Brazil': 'BRL', 'Mexico': 'MXN', 'Argentina': 'ARS',
  'Colombia': 'COP', 'Chile': 'CLP', 'Peru': 'PEN',
  'United Arab Emirates': 'AED', 'Saudi Arabia': 'SAR', 'Qatar': 'QAR',
  'Kuwait': 'KWD', 'Israel': 'ILS', 'Turkey': 'TRY',
  'South Africa': 'ZAR', 'Nigeria': 'NGN', 'Kenya': 'KES',
  'Egypt': 'EGP', 'Morocco': 'MAD', 'Russia': 'RUB',
};

/**
 * Replace the $ symbol in an earnings string with the user's local currency symbol.
 * Only replaces if the string starts with $ (base USD format).
 * @param {string} earningsStr - e.g. "$150-600/day"
 * @param {string} currencyCode - e.g. "GBP"
 * @returns {string} - e.g. "£150-600/day"
 */
export function convertCurrencySymbol(earningsStr, currencyCode) {
  if (!earningsStr || !currencyCode) return earningsStr || '';
  const symbol = CURRENCY_SYMBOLS[currencyCode];
  if (!symbol || symbol === '$') return earningsStr;
  // Replace leading $ with the target symbol
  return earningsStr.replace(/^\$/, symbol);
}

/**
 * Get the currency code for a country name.
 * @param {string} countryName - e.g. "United Kingdom"
 * @returns {string} - e.g. "GBP"
 */
export function getCurrencyForCountry(countryName) {
  if (!countryName) return 'USD';
  return CURRENCY_FOR_COUNTRY[countryName] || 'USD';
}


export function getCountryCode(countryName) {
  if (!countryName) return '';
  return COUNTRY_TO_CODE[countryName] || '';
}

/**
 * Get localized earnings for a specific country from opportunity data.
 * Returns { currency, range } or null if not available.
 */
export function getLocalizedEarnings(opp, countryName) {
  if (!opp?.localized_earnings || !countryName) return null;
  const code = getCountryCode(countryName);
  if (!code) return null;
  const data = opp.localized_earnings[code];
  return data && data.range ? data : null;
}

/**
 * Get regional note for a specific country from opportunity data.
 */
export function getRegionalNote(opp, countryName) {
  if (!opp?.regional_notes || !countryName) return null;
  const code = getCountryCode(countryName);
  if (!code) return null;
  return opp.regional_notes[code] || null;
}

/**
 * Check if a country matches any entry in an opportunity's countries list.
 * Handles both full names ("United Kingdom") and codes ("UK").
 */
export function isCountryInList(countryName, countriesList) {
  if (!countryName || !countriesList || countriesList.length === 0) return false;
  const nameLower = countryName.toLowerCase();
  const code = getCountryCode(countryName);
  const codeLower = code ? code.toLowerCase() : '';
  return countriesList.some(c => {
    const cl = c.toLowerCase();
    return cl === nameLower || cl === codeLower;
  });
}
